import { useEffect, useRef } from 'react';
const sourceId = 'terrain';

/**
 * Mapterhorn serves terrarium-encoded elevation, built over France from the IGN RGE ALTI 1m —
 * the alternatives (AWS Terrain Tiles, Copernicus GLO-30) fall back to a 30m grid here, which is
 * too coarse to read a valley at city zoom levels.
 */
const tilesUrl = 'https://tiles.mapterhorn.com/{z}/{x}/{y}.webp';
const attribution = '<a href="https://mapterhorn.com/attribution" target="_blank">&copy; Mapterhorn</a>';

/** Above this the endpoint 404s. MapLibre overzooms the tiles it has on its own. */
const maxzoom = 16;
const tileSize = 512;

/**
 * Cycling happens on far gentler slopes than the alpine scenes terrain rendering is usually shown
 * off with: left unexaggerated, a city looks flat even where the ride does not feel like it.
 */
const defaultExaggeration = 1.4;

/**
 * The sky is a property of the pitched view, not of the relief: any map can be tilted past the
 * horizon with a right-click drag, and with no sky declared what shows up there is the canvas
 * clear color rather than a sky. So it is applied once, for good, independently of the relief.
 *
 * Above the horizon MapLibre paints the `sky-color` → `horizon-color` gradient; below it the
 * terrain shader hazes the ground with `fog-color`, starting at `fog-ground-blend` (0 = map center,
 * 1 = horizon) once the pitch passes 60°. That haze is what washed the map out as soon as the
 * relief was turned on; pinning `fog-ground-blend` to 1 — the value MapLibre itself uses when a
 * style declares no sky — keeps the ground exactly as it renders without relief. `fog-color` and
 * `horizon-fog-blend` only feed that haze, so they are left out.
 */
const sky = {
  'sky-color': '#9fc6ea',
  'horizon-color': '#e8eef3',
  'sky-horizon-blend': 0.6,
  'fog-ground-blend': 1,
  'atmosphere-blend': 0.8
};

/**
 * 3D terrain, off by default.
 *
 * The source is kept around once added: toggling the relief back on is common enough that
 * re-downloading the elevation tiles every time is not worth the few hundred kilobytes saved.
 */
export function useTerrain(map, showed, {
  exaggeration = defaultExaggeration
} = {}) {
  const added = useRef(false);
  useEffect(() => {
    // `map` is only handed over once the style has loaded, and the style is never swapped
    // afterwards, so this holds for the whole life of the map.
    if (!map) return;
    map.setSky(sky);
  }, [map]);
  useEffect(() => {
    // `map` is only handed over once the style has loaded, so sources can be added right away.
    if (!map) return;
    if (!showed) {
      if (added.current) map.setTerrain(null);
      return;
    }
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'raster-dem',
        tiles: [tilesUrl],
        encoding: 'terrarium',
        tileSize,
        maxzoom,
        attribution
      });
    }
    map.setTerrain({
      source: sourceId,
      exaggeration
    });
    added.current = true;
  }, [map, showed, exaggeration]);
}